import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {number | undefined} */
  #stickyRefId;

  /** @type {number | null} */
  #lastStickyTop = null;

  /** @type {MutationObserver | undefined} */
  #resultsCountObserver;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#initStickyObserver();
    this.#updateVisibility();
    this.#observeResultsCount();
    this.setAttribute('initialized', '');
  }

  /**
   * Section Rendering morphs this element in place and doesn't reliably fire
   * `updatedCallback`. Observe `data-results-count` and `class` (the server re-adds
   * `hidden` on every zero-result render) to re-evaluate visibility after a morph.
   */
  #observeResultsCount() {
    if (!this.hasAttribute('hide-when-empty')) return;

    this.#resultsCountObserver?.disconnect();
    this.#resultsCountObserver = new MutationObserver(() => this.#updateVisibility());
    this.#resultsCountObserver.observe(this, { attributes: true, attributeFilter: ['data-results-count', 'class'] });
  }

  /**
   * Search page only (`hide-when-empty`). Hides the list for a genuine no-match (no
   * results, no filters) but keeps it visible when filters return nothing, and
   * toggles separate "show for no results" sections to mirror product-list.liquid.
   * Uses the `.hidden` utility class because `.product-grid-container` sets a
   * `display` value that overrides the `[hidden]` attribute.
   */
  #updateVisibility() {
    if (!this.hasAttribute('hide-when-empty')) return;

    const count = Number(this.getAttribute('data-results-count') ?? '0');
    const params = new URLSearchParams(window.location.search);
    const hasActiveFilters = [...params.keys()].some((key) => key.startsWith('filter.'));

    this.classList.toggle('hidden', count === 0 && !hasActiveFilters);

    for (const section of document.querySelectorAll('[data-show-for-no-results]')) {
      section.classList.toggle('hidden', count > 1);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    window.removeEventListener('scroll', this.#onStickyScroll);
    this.#resultsCountObserver?.disconnect();
    if (this.#stickyRefId) cancelAnimationFrame(this.#stickyRefId);
    document.querySelector('header-component')?.classList.remove('_has-sticky-facets');
  }

  /**
   * Initialize sticky tracking for visible .facets--stick-to-header.
   * _has-sticky-facets is active when the facets are being sticked to the header
   */
  #initStickyObserver() {
    window.addEventListener('scroll', this.#onStickyScroll, { passive: true });
    window.addEventListener('resize', () => {
      // Reset the last sticky top position on resize to ensure the header class is updated correctly
      this.#lastStickyTop = null;
      this.#onStickyScroll();
    });
  }

  #onStickyScroll = () => {
    requestAnimationFrame(() => {
      const headerComponent = document.querySelector('header-component');
      const facetsElement = [...document.querySelectorAll('.facets--stick-to-header')].find(
        (el) => el.offsetParent !== null,
      );
      if (!headerComponent || !facetsElement) return;

      const stickyTop = facetsElement.getBoundingClientRect().top;
      headerComponent.classList.toggle('_has-sticky-facets', stickyTop === this.#lastStickyTop);
      this.#lastStickyTop = stickyTop;
    });
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
